import { useState } from "react";

interface Props {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  status?: string;
  output?: unknown;
  durationMs?: number;
}

export function ToolCallPanel({ toolCallId, toolName, args, status, output, durationMs }: Props) {
  const [expanded, setExpanded] = useState(false);
  const isComplete = !!status;
  const isSuccess = status === "success";

  return (
    <div className="animate-slide-up rounded-xl border border-ink-100 bg-white shadow-subtle overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-1 transition-colors"
      >
        {/* Status indicator */}
        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${
          !isComplete
            ? "bg-blue-50"
            : isSuccess
              ? "bg-emerald-50"
              : "bg-red-50"
        }`}>
          {!isComplete ? (
            <SpinnerIcon className="h-3.5 w-3.5 animate-spin text-blue-600" />
          ) : isSuccess ? (
            <CheckIcon className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <XIcon className="h-3.5 w-3.5 text-red-600" />
          )}
        </span>

        {/* Tool info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink-800">{toolName}</span>
            {isComplete && (
              <span className={isSuccess ? "badge-green" : "badge-red"}>
                {status}
              </span>
            )}
          </div>
          <span className="text-2xs font-mono text-ink-400">{toolCallId.slice(0, 16)}</span>
        </div>

        {/* Duration */}
        {durationMs !== undefined && (
          <span className="text-xs tabular-nums text-ink-400">{durationMs}ms</span>
        )}

        {/* Chevron */}
        <svg
          className={`h-4 w-4 shrink-0 text-ink-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {/* Expandable detail */}
      {expanded && (
        <div className="border-t border-ink-100 bg-surface-1 px-4 py-3 space-y-3">
          <div>
            <p className="mb-1 text-2xs font-semibold uppercase tracking-wider text-ink-400">Arguments</p>
            <pre className="overflow-x-auto rounded-lg bg-ink-950 px-3 py-2 font-mono text-xs text-emerald-300">
              {JSON.stringify(args, null, 2)}
            </pre>
          </div>
          {output !== undefined && (
            <div>
              <p className="mb-1 text-2xs font-semibold uppercase tracking-wider text-ink-400">Output</p>
              <pre className="overflow-x-auto rounded-lg bg-ink-950 px-3 py-2 font-mono text-xs text-sky-300">
                {typeof output === "string" ? output : JSON.stringify(output, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 2a6 6 0 1 0 6 6" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}
